import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Question, Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { api } from "@/lib/gado";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const EMPTY_FORM = { question: "", answer: "", category: "Geral", order: 0, published: true };

export default function AdminFaq() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null); // null | {faq_id?} + form
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);

  const { data: faqs = [], isLoading } = useQuery({
    queryKey: ["admin-faqs"],
    queryFn: async () => (await api.get("/admin/faqs")).data,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-faqs"] });

  const openCreate = () => {
    setForm({ ...EMPTY_FORM, order: faqs.length });
    setEditing({});
  };

  const openEdit = (f) => {
    setForm({ question: f.question, answer: f.answer, category: f.category || "Geral", order: f.order || 0, published: !!f.published });
    setEditing(f);
  };

  const save = async () => {
    if (!form.question.trim() || !form.answer.trim()) {
      toast.error("Informe pergunta e resposta");
      return;
    }
    setSaving(true);
    try {
      if (editing?.faq_id) {
        await api.put(`/admin/faqs/${editing.faq_id}`, form);
        toast.success("Pergunta atualizada");
      } else {
        await api.post("/admin/faqs", form);
        toast.success("Pergunta criada");
      }
      setEditing(null);
      refresh();
    } catch {
      toast.error("Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const togglePublished = async (f, published) => {
    try {
      await api.put(`/admin/faqs/${f.faq_id}`, {
        question: f.question, answer: f.answer, category: f.category, order: f.order, published,
      });
      toast.success(published ? "Pergunta publicada" : "Pergunta despublicada");
      refresh();
    } catch {
      toast.error("Falha ao atualizar");
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setSaving(true);
    try {
      await api.delete(`/admin/faqs/${deleting.faq_id}`);
      toast.success("Pergunta removida");
      setDeleting(null);
      refresh();
    } catch {
      toast.error("Falha ao remover");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-faq-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-cabinet text-2xl md:text-3xl font-bold text-gado-ink tracking-tight">FAQ</h1>
          <p className="text-gado-muted text-sm">Perguntas frequentes exibidas na tela de Ajuda do app.</p>
        </div>
        <Button onClick={openCreate} data-testid="faq-new-button" className="shrink-0 bg-gado-green hover:bg-gado-greenDark text-white">
          <Plus size={16} className="mr-1.5" /> Nova pergunta
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => <div key={i} className="h-16 rounded-xl bg-gado-line/40 animate-pulse" />)}
        </div>
      ) : faqs.length === 0 ? (
        <div className="rounded-xl border border-gado-line bg-white p-10 text-center" data-testid="faq-empty">
          <Question size={32} className="mx-auto text-gado-muted mb-2" />
          <p className="text-sm text-gado-muted">Nenhuma pergunta cadastrada ainda.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {faqs.map((f) => (
            <div
              key={f.faq_id}
              className="rounded-xl border border-gado-line bg-white p-4 flex items-start gap-3"
              data-testid={`faq-item-${f.faq_id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-gado-ink text-sm">{f.question}</p>
                  <Badge variant="outline" className="border-gado-line bg-gado-bg text-gado-muted shrink-0">
                    {f.category || "Geral"}
                  </Badge>
                </div>
                <p className="text-xs text-gado-muted mt-1 line-clamp-2">{f.answer}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <div className="flex items-center gap-1.5">
                  <Switch
                    checked={!!f.published}
                    onCheckedChange={(v) => togglePublished(f, v)}
                    data-testid={`faq-published-${f.faq_id}`}
                    aria-label="Publicada"
                  />
                  <span className="text-[10px] uppercase tracking-wider text-gado-muted font-semibold hidden sm:inline">
                    {f.published ? "Visível" : "Oculta"}
                  </span>
                </div>
                <Button size="sm" variant="outline" onClick={() => openEdit(f)} data-testid={`faq-edit-${f.faq_id}`} aria-label="Editar">
                  <PencilSimple size={14} />
                </Button>
                <Button size="sm" variant="outline" onClick={() => setDeleting(f)} data-testid={`faq-delete-${f.faq_id}`} aria-label="Remover"
                  className="text-destructive">
                  <Trash size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dialog: criar/editar */}
      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent data-testid="faq-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-cabinet">{editing?.faq_id ? "Editar pergunta" : "Nova pergunta"}</DialogTitle>
            <DialogDescription>Conteúdo exibido na tela de Ajuda do usuário.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.16em] font-semibold text-gado-muted">Pergunta</label>
              <Input
                value={form.question}
                onChange={(e) => setForm({ ...form, question: e.target.value })}
                placeholder="Ex.: Como funciona o limite de PDFs?"
                className="mt-1 bg-white"
                data-testid="faq-question-input"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.16em] font-semibold text-gado-muted">Resposta</label>
              <Textarea
                value={form.answer}
                onChange={(e) => setForm({ ...form, answer: e.target.value })}
                rows={4}
                placeholder="Resposta clara e direta…"
                className="mt-1 bg-white"
                data-testid="faq-answer-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-[0.16em] font-semibold text-gado-muted">Categoria</label>
                <Input
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="mt-1 bg-white"
                  data-testid="faq-category-input"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.16em] font-semibold text-gado-muted">Ordem</label>
                <Input
                  type="number"
                  value={form.order}
                  onChange={(e) => setForm({ ...form, order: Number(e.target.value) || 0 })}
                  className="mt-1 bg-white"
                  data-testid="faq-order-input"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={form.published}
                onCheckedChange={(v) => setForm({ ...form, published: v })}
                data-testid="faq-published-switch"
              />
              <span className="text-sm text-gado-ink">Publicada (visível no app)</span>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving} data-testid="faq-save-button"
              className="w-full bg-gado-green hover:bg-gado-greenDark text-white">
              {saving ? "Salvando…" : "Salvar pergunta"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: confirmar exclusão */}
      <Dialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <DialogContent data-testid="faq-delete-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-cabinet">Remover pergunta</DialogTitle>
            <DialogDescription>
              "{deleting?.question}" será removida da tela de Ajuda. Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={confirmDelete} disabled={saving} data-testid="faq-delete-confirm"
              className="w-full bg-destructive hover:bg-destructive/90 text-white">
              {saving ? "Removendo…" : "Remover"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
